import { useEffect, useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  Mail,
  Lock,
  Key,
  Eye,
  EyeOff,
  ArrowRight,
  User,
  UserPlus,
  TrendingUp,
  LogIn,
} from "lucide-react";
import API from "../services/api";
import { useToast } from "../components/Toast";
import {
  AUTH_LIMITS,
  hasValidEmailFormat,
  normalizeEmail,
  sanitizeAuthField,
} from "../utils/authValidation";
import "./Auth.css";

export default function Signup() {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultSponsorCode = sanitizeAuthField("sponsorCode", searchParams.get("ref") || "");
  const defaultEmail = sanitizeAuthField("email", searchParams.get("email") || "");
  const defaultOtp = sanitizeAuthField("otp", searchParams.get("code") || "");
  const defaultStep = searchParams.get("step") === "verify" && defaultEmail ? 2 : 1;

  const [step, setStep] = useState(defaultStep);
  const [form, setForm] = useState({
    name: "",
    email: defaultEmail,
    otp: defaultOtp,
    password: "",
    sponsorCode: defaultSponsorCode,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [xLoading, setXLoading] = useState(false);
  const sponsorCodeRef = useRef(defaultSponsorCode);

  useEffect(() => {
    sponsorCodeRef.current = form.sponsorCode;
  }, [form.sponsorCode]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: sanitizeAuthField(name, value) }));
  };

  const handleXSignup = () => {
    if (!API.defaults.baseURL) {
      toast.error("X sign-up is not available right now.");
      return;
    }

    setXLoading(true);

    const params = new URLSearchParams({ purpose: "signup" });
    const referralCode = sponsorCodeRef.current.trim();
    if (referralCode) params.set("referralCode", referralCode);

    window.location.assign(`${API.defaults.baseURL}/auth/x?${params.toString()}`);
  };

  const handleGoogleSignup = () => {
    if (!API.defaults.baseURL) {
      toast.error("Google sign-up is not available right now.");
      return;
    }

    setGoogleLoading(true);

    const params = new URLSearchParams({ purpose: "signup" });
    const referralCode = sponsorCodeRef.current.trim();
    if (referralCode) params.set("referralCode", referralCode);

    window.location.assign(`${API.defaults.baseURL}/auth/google?${params.toString()}`);
  };

  const getValidatedEmail = () => {
    const email = normalizeEmail(form.email);

    if (!email) {
      toast.error("Please enter your email.");
      return null;
    }

    if (!hasValidEmailFormat(email)) {
      toast.error("Please enter a valid email address.");
      return null;
    }

    return email;
  };

  const sendOtp = async () => {
    const email = getValidatedEmail();
    if (!email) return;

    try {
      setLoading(true);
      await API.post("/auth/request-otp", {
        email,
        purpose: "signup",
      });
      toast.success("Verification code sent to your email.");
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.msg || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const verifyAndSignup = async () => {
    const { name, otp, password, sponsorCode } = form;
    const normalizedEmail = getValidatedEmail();

    if (!normalizedEmail) return;

    if (!otp || !password) {
      toast.error("Enter OTP and new password.");
      return;
    }

    if (otp.length !== AUTH_LIMITS.verificationCode) {
      toast.error("Verification code must be 6 digits.");
      return;
    }

    try {
      setLoading(true);
      await API.post("/auth/verify-otp", { email: normalizedEmail, code: otp });
      await API.post("/auth/signup", {
        name: sanitizeAuthField("name", name).trim(),
        email: normalizedEmail,
        password,
        sponsorCode: sanitizeAuthField("sponsorCode", sponsorCode).trim() || undefined,
      });
      navigate("/login", {
        replace: true,
        state: {
          email: normalizedEmail,
          toast: {
            type: "warning",
            message: "Your account is not activated. Please contact the admin to activate it.",
          },
        },
      });
    } catch (err) {
      toast.error(err.response?.data?.msg || "Signup failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-shell">
        <aside className="auth-aside">
          <div className="auth-aside-copy">
            <div className="auth-kicker">
              <UserPlus size={16} />
              Account setup
            </div>
            <h1>Set up your workspace.</h1>
            <p>Create access, verify email, and get your analysis flow ready.</p>

            <div className="auth-visual" aria-hidden="true">
              <lottie-player
                src="/trading.json"
                background="transparent"
                speed="1"
                loop
                autoplay
                className="auth-lottie"
              ></lottie-player>
            </div>
          </div>
        </aside>

        <section className="auth-panel">
          <div className="auth-panel-content">
            <div className="auth-mobile-mark" aria-hidden="true">
              <span className="auth-brand-mark">
                <TrendingUp size={24} />
              </span>
            </div>

            <div className="auth-header">
              <h2>{step === 1 ? "Create Account" : "Verify Email"}</h2>
              <p>
                {step === 1
                  ? "Join Premium Profit and get your account ready."
                  : "Enter the verification code and set your password."}
              </p>
            </div>

            {step === 1 && (
              <>
                <div className="google-signin-section">
                  <div className="auth-provider-grid">
                    <button
                      type="button"
                      className="google-auth-button"
                      disabled={googleLoading || xLoading || loading}
                      onClick={handleGoogleSignup}
                    >
                      {googleLoading ? (
                        <div className="auth-loading-spinner auth-loading-spinner-social google-spinner"></div>
                      ) : (
                        <>
                          <svg
                            className="google-icon"
                            viewBox="-3 0 262 262"
                            xmlns="http://www.w3.org/2000/svg"
                            preserveAspectRatio="xMidYMid"
                            aria-hidden="true"
                          >
                            <path
                              d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"
                              fill="currentColor"
                            />
                            <path
                              d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"
                              fill="currentColor"
                            />
                            <path
                              d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782"
                              fill="currentColor"
                            />
                            <path
                              d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"
                              fill="currentColor"
                            />
                          </svg>
                          Google
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      className="google-auth-button x-auth-button"
                      disabled={xLoading || googleLoading || loading}
                      onClick={handleXSignup}
                    >
                      {xLoading ? (
                        <div className="auth-loading-spinner auth-loading-spinner-social x-spinner"></div>
                      ) : (
                        <>
                          <svg
                            className="x-icon"
                            viewBox="0 0 300 271"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <path
                              fill="currentColor"
                              d="m236 0h46l-101 115 118 156h-92.6l-72.5-94.8-83 94.8h-46l107-123-113-148h94.9l65.5 86.6zm-16.1 244h25.5l-165-218h-27.4z"
                            />
                          </svg>
                          <span className="x-auth-label">
                            <span>Twitter</span>
                            <span className="x-auth-slash" aria-hidden="true">
                              /
                            </span>
                            <span>X</span>
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    sendOtp();
                  }}
                  className="auth-form"
                >
                  <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <div className="input-wrapper">
                      <User className="input-icon" />
                      <input
                        id="name"
                        name="name"
                        type="text"
                        value={form.name}
                        onChange={onChange}
                        placeholder="Enter your name"
                        maxLength={AUTH_LIMITS.name}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <div className="input-wrapper">
                      <Mail className="input-icon" />
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={onChange}
                        placeholder="Enter your email"
                        required
                        maxLength={AUTH_LIMITS.email}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="sponsorCode">
                      Referral Code <span className="form-label-muted">(optional)</span>
                    </label>
                    <div className="input-wrapper">
                      <Key className="input-icon" />
                      <input
                        id="sponsorCode"
                        name="sponsorCode"
                        type="text"
                        value={form.sponsorCode}
                        onChange={onChange}
                        placeholder="Enter referral code"
                        disabled={loading}
                        maxLength={AUTH_LIMITS.referralCode}
                      />
                    </div>
                  </div>

                  <button type="submit" className="auth-button" disabled={loading}>
                    {loading ? (
                      <>
                        <div className="auth-loading-spinner"></div>
                        Sending OTP...
                      </>
                    ) : (
                      <>
                        Send Verification Code
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </form>

                <div className="auth-links">
                  <div className="auth-divider">
                    <span>Already have an account?</span>
                  </div>
                  <Link to="/login" className="signup-link">
                    <LogIn size={17} />
                    Sign In
                  </Link>

                  <div className="auth-legal-links">
                    <Link to="/privacy-policy">Privacy Policy</Link>
                    <span className="auth-legal-dot">&middot;</span>
                    <Link to="/terms-of-service">Terms of Service</Link>
                  </div>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    verifyAndSignup();
                  }}
                  className="auth-form"
                >
                  <div className="form-group">
                    <label htmlFor="otp">Verification Code</label>
                    <div className="input-wrapper">
                      <Key className="input-icon" />
                      <input
                        id="otp"
                        name="otp"
                        type="text"
                        value={form.otp}
                        onChange={onChange}
                        placeholder="Enter 6-digit code"
                        required
                        disabled={loading}
                        maxLength={AUTH_LIMITS.verificationCode}
                        inputMode="numeric"
                        pattern={`[0-9]{${AUTH_LIMITS.verificationCode}}`}
                        title="Please enter a 6-digit verification code."
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="password">Create Password</label>
                    <div className="input-wrapper">
                      <Lock className="input-icon" />
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={form.password}
                        onChange={onChange}
                        placeholder="Choose a strong password"
                        required
                        maxLength={AUTH_LIMITS.password}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="sponsorCodeStep2">
                      Referral Code <span className="form-label-muted">(optional)</span>
                    </label>
                    <div className="input-wrapper">
                      <Key className="input-icon" />
                      <input
                        id="sponsorCodeStep2"
                        name="sponsorCode"
                        type="text"
                        value={form.sponsorCode}
                        onChange={onChange}
                        placeholder="Enter referral code"
                        disabled={loading}
                        maxLength={AUTH_LIMITS.referralCode}
                      />
                    </div>
                  </div>

                  <button type="submit" className="auth-button" disabled={loading}>
                    {loading ? (
                      <>
                        <div className="auth-loading-spinner"></div>
                        Creating Account...
                      </>
                    ) : (
                      <>
                        Create Account
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </form>

                <div className="auth-links">
                  <button onClick={() => setStep(1)} className="auth-inline-action">
                    Back to Email
                  </button>

                  <div className="auth-divider">
                    <span>Already have an account?</span>
                  </div>

                  <Link to="/login" className="signup-link">
                    <LogIn size={17} />
                    Sign In
                  </Link>

                  <div className="auth-legal-links">
                    <Link to="/privacy-policy">Privacy Policy</Link>
                    <span className="auth-legal-dot">&middot;</span>
                    <Link to="/terms-of-service">Terms of Service</Link>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
